import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Loader2, AlertTriangle, Plus, Pencil, Trash2, RefreshCw, Check, Shield,
  ArrowUpRight, Eye, EyeOff, Send, Gavel, History, X,
} from "lucide-react";
import { toast } from "sonner";
import { api } from "../../lib/api";

const STATUSES = ["Help Identify", "Identified", "Arrested — Case Pending", "Charged", "On Trial", "Convicted"];

const BLANK = {
  title: "",
  status: "Help Identify",
  city: "",
  state: "",
  state_code: "",
  incident_date: "",
  incident_time: "",
  incident_location: "",
  animals_affected: "",
  summary: "",
  suspect_description: "",
  vehicle_description: "",
  license_plate: "",
  agency_name: "",
  agency_phone: "",
  agency_tip_url: "",
  case_number: "",
  source_url: "",
  photo_url: "",
  photo_caption: "",
  photo_source_url: "",
  photo_attribution: "",
  case_updates: [],
};

export default function AdminActiveCasesList({ token }) {
  const headers = useMemo(() => ({ "X-Admin-Token": token }), [token]);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [busyId, setBusyId] = useState(null);
  const [editing, setEditing] = useState(null);
  const [creating, setCreating] = useState(false);

  const load = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    try {
      const { data } = await api.get("/admin/active-cases", { headers });
      setItems(data.items || []);
    } catch { toast.error("Failed to load active cases."); }
    finally { setLoading(false); }
  }, [token, headers]);

  useEffect(() => { load(); }, [load]);

  const del = async (c) => {
    if (!window.confirm(`Delete active case "${c.title}"? This cannot be undone.`)) return;
    setBusyId(c.id);
    try {
      await api.delete(`/admin/active-cases/${c.id}`, { headers });
      toast.success("Deleted.");
      load();
    } catch { toast.error("Delete failed."); }
    finally { setBusyId(null); }
  };

  const discardEdits = async (c) => {
    if (!window.confirm(`Discard the staged edits for "${c.title}"? The public page will remain exactly as it is now.`)) return;
    setBusyId(c.id);
    try {
      await api.post(`/admin/active-cases/${c.id}/discard-edits`, {}, { headers });
      toast.success("Staged edits discarded.");
      load();
    } catch { toast.error("Discard failed."); }
    finally { setBusyId(null); }
  };

  const publish = async (c, { sendAlert }) => {
    // Detect whether re-publishing will change the status (via staged edits).
    const stagedStatus = (c.pending_edits && c.pending_edits.status) || c.status;
    const statusChanging = c.is_published && stagedStatus !== c.status;
    let effectiveSendAlert = sendAlert;
    const confirmHeadline = c.is_published
      ? `Re-publish "${c.title}"? Any staged edits will go live publicly.`
      : `Publish "${c.title}"?`;
    const confirmAlertNote =
      effectiveSendAlert
        ? ` Alert emails will be sent to subscribers in ${c.state_code || c.state}.`
        : "";
    // When a status transition is in flight on a re-publish, offer the admin
    // the option to send a status-update alert even if they hadn't opted in.
    if (statusChanging) {
      const wantsAlert = window.confirm(
        `Status will change: ${c.status} → ${stagedStatus}\n\n` +
        `Send a status-update alert email to all subscribers in ${c.state_code || c.state}?\n\n` +
        `Click OK to send alerts, Cancel to re-publish silently.`
      );
      effectiveSendAlert = wantsAlert;
    } else {
      if (!window.confirm(confirmHeadline + confirmAlertNote)) return;
    }
    setBusyId(c.id);
    try {
      const { data } = await api.post(`/admin/active-cases/${c.id}/publish`, { send_alert: !!effectiveSendAlert }, { headers });
      const alert = data.alert;
      const noteBits = [];
      if (data.status_changed) noteBits.push(`Status: ${data.previous_status} → ${data.current_status}`);
      if (alert) noteBits.push(`Alerts sent ${alert.sent || 0}, skipped ${alert.skipped || 0}, failed ${alert.failed || 0}`);
      toast.success(noteBits.length ? `Published. ${noteBits.join(" · ")}` : "Published.");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Publish failed.");
    } finally { setBusyId(null); }
  };

  const unpublish = async (c) => {
    if (!window.confirm(`Un-publish "${c.title}"? The public page will become inaccessible immediately.`)) return;
    setBusyId(c.id);
    try {
      await api.post(`/admin/active-cases/${c.id}/unpublish`, {}, { headers });
      toast.success("Un-published.");
      load();
    } catch { toast.error("Un-publish failed."); }
    finally { setBusyId(null); }
  };

  const resendAlert = async (c) => {
    if (!window.confirm(
      `Send a NEW alert email for "${c.title}" to all active subscribers in ${c.state_code || c.state}?\n\n` +
      `Last alert: ${c.last_alert_sent_at ? new Date(c.last_alert_sent_at).toLocaleString() + " (" + (c.last_alert_recipient_count || 0) + " recipients)" : "never"}.\n\n` +
      `Click OK only if you intend to send a fresh alert — subscribers may receive duplicates.`
    )) return;
    setBusyId(c.id);
    try {
      const { data } = await api.post(`/admin/active-cases/${c.id}/send-alert`, {}, { headers });
      const r = data.alert || {};
      toast.success(`Alert sent: ${r.sent || 0}, skipped: ${r.skipped || 0}, failed: ${r.failed || 0}.`);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Alert send failed.");
    } finally { setBusyId(null); }
  };

  const promote = async (c) => {
    if (c.promoted_to_abuser_id) {
      toast.info(`Already promoted to registry (${c.promoted_to_abuser_id}).`);
      return;
    }
    if (c.status !== "Convicted") {
      toast.error("Only Convicted cases can be promoted.");
      return;
    }
    if (!window.confirm(
      `Promote "${c.title}" to the PERMANENT registry?\n\n` +
      `A new abusers record will be created and linked. The Active Case will remain available ` +
      `at /active-cases/${c.id} as a public history.\n\n` +
      `This cannot be undone from the dashboard.`
    )) return;
    const year = window.prompt("Conviction year (optional)", String(new Date().getFullYear())) || "";
    const sentencing = window.prompt("Sentencing summary (optional)", "") || "";
    setBusyId(c.id);
    try {
      const { data } = await api.post(`/admin/active-cases/${c.id}/promote`, {
        confirm: true,
        conviction_year: parseInt(year) || null,
        sentencing: sentencing || null,
      }, { headers });
      toast.success(`Promoted to registry (${data.abuser_id}). Please update the suspect name on the registry side.`);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Promote failed.");
    } finally { setBusyId(null); }
  };

  return (
    <div className="border border-zinc-900 bg-white" data-testid="admin-active-cases">
      <div className="px-5 py-3 border-b border-zinc-900 flex items-center gap-3 flex-wrap justify-between">
        <div className="flex items-center gap-2">
          <AlertTriangle size={14} className="text-red-600" />
          <span className="label-eyebrow">Active Cases · Help Identify</span>
          <span className="text-xs text-zinc-500 font-mono">({items.length})</span>
        </div>
        <div className="flex items-center gap-2">
          <button type="button" onClick={load} className="btn-outline text-xs inline-flex items-center gap-1" data-testid="admin-active-refresh">
            <RefreshCw size={12}/> Refresh
          </button>
          <button
            type="button"
            onClick={() => setCreating(true)}
            className="inline-flex items-center gap-1 bg-red-600 text-white text-xs font-bold uppercase tracking-widest px-3 py-1.5 border border-red-700 hover:bg-red-700"
            data-testid="admin-active-add"
          >
            <Plus size={12}/> Add Case
          </button>
        </div>
      </div>

      <div className="divide-y divide-zinc-200">
        {loading && (
          <div className="p-6 text-sm text-zinc-500 inline-flex items-center gap-2">
            <Loader2 size={14} className="animate-spin"/> Loading…
          </div>
        )}
        {!loading && items.length === 0 && (
          <div className="p-6 text-sm text-zinc-500">No active cases yet.</div>
        )}
        {!loading && items.map((c) => (
          <div key={c.id} className="p-4 flex flex-col lg:flex-row lg:items-start gap-4" data-testid={`admin-active-row-${c.id}`}>
            <div className="flex-shrink-0 w-20 h-20 border border-zinc-300 bg-zinc-50 overflow-hidden">
              {c.photo_url ? (
                <img src={c.photo_url} alt="" className="w-full h-full object-cover" referrerPolicy="no-referrer"/>
              ) : (
                <div className="w-full h-full flex items-center justify-center text-zinc-400">
                  <AlertTriangle size={20}/>
                </div>
              )}
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <h4 className="font-display font-black text-base leading-tight">{c.title}</h4>
                <StatusPill status={c.status}/>
                {!c.is_published && (
                  <span className="text-[10px] uppercase tracking-widest font-bold px-2 py-0.5 border border-zinc-900 bg-zinc-900 text-white">Draft</span>
                )}
                {c.is_published && c.has_pending_edits && (
                  <span
                    className="text-[10px] uppercase tracking-widest font-bold px-2 py-0.5 border border-amber-700 bg-amber-100 text-amber-900"
                    data-testid={`admin-active-pending-${c.id}`}
                  >
                    Pending edits
                  </span>
                )}
                {c.promoted_to_abuser_id && (
                  <span className="text-[10px] uppercase tracking-widest font-bold px-2 py-0.5 border border-green-700 bg-green-50 text-green-800">
                    In registry
                  </span>
                )}
              </div>
              <div className="text-xs text-zinc-600 mt-1">
                {c.city}, {c.state_code || c.state} · {c.incident_date}
              </div>
              <div className="text-xs text-zinc-500 mt-1 inline-flex items-center gap-1">
                <Shield size={10}/> {c.agency_name} · {c.agency_phone || "no phone"}
              </div>
              <p className="text-sm text-zinc-700 mt-2 line-clamp-2">{c.summary}</p>
              {c.last_alert_sent_at && (
                <div className="text-[11px] text-zinc-500 mt-2 inline-flex items-center gap-1">
                  <Send size={10}/> Last alert: {new Date(c.last_alert_sent_at).toLocaleString()} · {c.last_alert_recipient_count || 0} recipients
                </div>
              )}
            </div>
            <div className="flex flex-col gap-2 lg:min-w-[240px]">
              {!c.is_published ? (
                <button
                  type="button"
                  disabled={busyId === c.id}
                  onClick={() => publish(c, { sendAlert: true })}
                  className="inline-flex items-center justify-center gap-1 bg-green-700 text-white text-xs font-bold uppercase tracking-widest px-3 py-2 hover:bg-green-800 disabled:opacity-60"
                  data-testid={`admin-active-publish-${c.id}`}
                >
                  <Eye size={12}/> Publish + send alerts
                </button>
              ) : c.has_pending_edits ? (
                <>
                  <button
                    type="button"
                    disabled={busyId === c.id}
                    onClick={() => publish(c, { sendAlert: false })}
                    className="inline-flex items-center justify-center gap-1 bg-amber-600 text-white text-xs font-bold uppercase tracking-widest px-3 py-2 hover:bg-amber-700 disabled:opacity-60"
                    data-testid={`admin-active-republish-${c.id}`}
                  >
                    <Check size={12}/> Save & Re-publish
                  </button>
                  <button
                    type="button"
                    disabled={busyId === c.id}
                    onClick={() => discardEdits(c)}
                    className="inline-flex items-center justify-center gap-1 text-xs font-bold uppercase tracking-widest px-3 py-2 border border-zinc-300 hover:border-red-600 hover:text-red-600 disabled:opacity-60"
                    data-testid={`admin-active-discard-${c.id}`}
                  >
                    <X size={12}/> Discard edits
                  </button>
                </>
              ) : (
                <button
                  type="button"
                  disabled={busyId === c.id}
                  onClick={() => unpublish(c)}
                  className="inline-flex items-center justify-center gap-1 btn-outline text-xs disabled:opacity-60"
                  data-testid={`admin-active-unpublish-${c.id}`}
                >
                  <EyeOff size={12}/> Un-publish
                </button>
              )}

              {c.is_published && !c.has_pending_edits && (
                <button
                  type="button"
                  disabled={busyId === c.id}
                  onClick={() => resendAlert(c)}
                  className="inline-flex items-center justify-center gap-1 text-xs font-bold uppercase tracking-widest px-3 py-2 border border-red-700 text-red-700 hover:bg-red-50 disabled:opacity-60"
                  data-testid={`admin-active-resend-${c.id}`}
                >
                  <Send size={12}/> Re-send alert
                </button>
              )}

              {c.status === "Convicted" && !c.promoted_to_abuser_id && (
                <button
                  type="button"
                  disabled={busyId === c.id}
                  onClick={() => promote(c)}
                  className="inline-flex items-center justify-center gap-1 text-xs font-bold uppercase tracking-widest px-3 py-2 border-2 border-zinc-900 bg-zinc-900 text-white hover:bg-red-600 hover:border-red-600 disabled:opacity-60"
                  data-testid={`admin-active-promote-${c.id}`}
                >
                  <Gavel size={12}/> Promote to Registry
                </button>
              )}

              <button
                type="button"
                onClick={() => setEditing(c)}
                className="inline-flex items-center justify-center gap-1 btn-outline text-xs"
                data-testid={`admin-active-edit-${c.id}`}
              >
                <Pencil size={12}/> Update Case
              </button>
              <a
                href={`/active-cases/${c.id}`}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center justify-center gap-1 text-xs font-bold uppercase tracking-widest px-3 py-2 border border-zinc-300 hover:border-red-600 hover:text-red-600"
              >
                <ArrowUpRight size={12}/> View Public
              </a>
              <button
                type="button"
                disabled={busyId === c.id}
                onClick={() => del(c)}
                className="inline-flex items-center justify-center gap-1 text-xs font-bold uppercase tracking-widest px-3 py-2 text-red-700 border border-red-700 hover:bg-red-50 disabled:opacity-60"
                data-testid={`admin-active-delete-${c.id}`}
              >
                <Trash2 size={12}/> Delete
              </button>
            </div>
          </div>
        ))}
      </div>

      {(editing || creating) && (
        <ActiveCaseEditModal
          initial={editing || BLANK}
          headers={headers}
          isNew={creating}
          onClose={() => { setEditing(null); setCreating(false); }}
          onSaved={() => { setEditing(null); setCreating(false); load(); }}
        />
      )}
    </div>
  );
}

function StatusPill({ status }) {
  const tone = {
    "Help Identify":            "border-red-700 bg-red-50 text-red-700",
    "Identified":               "border-amber-700 bg-amber-50 text-amber-800",
    "Arrested — Case Pending":  "border-amber-800 bg-amber-100 text-amber-900",
    "Charged":                  "border-amber-700 bg-amber-50 text-amber-800",
    "On Trial":                 "border-amber-700 bg-amber-50 text-amber-800",
    "Convicted":                "border-zinc-900 bg-zinc-900 text-white",
  }[status] || "border-zinc-300 bg-zinc-50 text-zinc-700";
  return <span className={`text-[10px] uppercase tracking-widest font-bold px-2 py-0.5 border ${tone}`}>{status}</span>;
}

function ActiveCaseEditModal({ initial, headers, isNew, onClose, onSaved }) {
  // Merge pending edits over live fields so the admin sees the latest working copy.
  const effective = { ...initial, ...(initial.pending_edits || {}) };
  const [form, setForm] = useState({ ...BLANK, ...effective, case_updates: effective.case_updates || [] });
  const [saving, setSaving] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [newUpdateDate, setNewUpdateDate] = useState(new Date().toISOString().slice(0, 10));
  const [newUpdateText, setNewUpdateText] = useState("");
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const addCaseUpdate = () => {
    const text = (newUpdateText || "").trim();
    if (!text) return;
    setForm((f) => ({
      ...f,
      case_updates: [...(f.case_updates || []), { date: newUpdateDate, text }],
    }));
    setNewUpdateText("");
  };
  const removeCaseUpdate = (idx) => {
    setForm((f) => ({ ...f, case_updates: (f.case_updates || []).filter((_, i) => i !== idx) }));
  };

  const isPublished = !!initial.is_published;
  const hasPending = !!(initial.pending_edits && Object.keys(initial.pending_edits).length);

  const save = async (e, { andPublish = false } = {}) => {
    if (e) e.preventDefault();
    if (!form.title || !form.city || !form.state || !form.incident_date || !form.summary || !form.agency_name) {
      toast.error("Title, city, state, incident date, summary and agency are required.");
      return;
    }
    const payload = Object.fromEntries(
      Object.entries(form).map(([k, v]) => [k, typeof v === "string" ? v.trim() : v])
    );
    setSaving(true);
    try {
      if (isNew) {
        const { data } = await api.post("/admin/active-cases", payload, { headers });
        toast.success("Case created as Draft.");
        if (andPublish) {
          if (!window.confirm("Publish this case immediately? Alert emails will be sent to subscribers in the matching state.")) {
            onSaved();
            return;
          }
          await api.post(`/admin/active-cases/${data.id}/publish`, { send_alert: true }, { headers });
          toast.success("Published + alerts sent.");
        }
      } else {
        await api.put(`/admin/active-cases/${initial.id}`, payload, { headers });
        toast.success(isPublished ? "Edits staged. Click Save & Re-publish on the row to go live." : "Draft saved.");
        if (andPublish) {
          const confirmMsg = isPublished
            ? "Re-publish this case? Your staged edits will go live publicly immediately."
            : "Publish this case? Alert emails will be sent to subscribers in the matching state.";
          if (!window.confirm(confirmMsg)) {
            onSaved();
            return;
          }
          await api.post(`/admin/active-cases/${initial.id}/publish`, { send_alert: !isPublished }, { headers });
          toast.success(isPublished ? "Re-published." : "Published + alerts sent.");
        }
      }
      onSaved();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[10000] bg-black/70 flex items-center justify-center p-4 overflow-y-auto" onClick={onClose} data-testid="admin-active-edit-modal">
      <div className="bg-white border-2 border-zinc-900 max-w-3xl w-full my-auto max-h-[92vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="bg-zinc-900 text-white px-5 py-4 flex items-center justify-between border-b border-red-600 sticky top-0 z-10">
          <div>
            <div className="label-eyebrow text-red-500 flex items-center gap-2">
              {isNew ? "Add" : "Update"} Active Case
              {!isNew && hasPending && (
                <span className="text-[10px] px-2 py-0.5 bg-amber-500 text-zinc-900 font-bold">Pending edits — not public yet</span>
              )}
              {!isNew && !isPublished && (
                <span className="text-[10px] px-2 py-0.5 bg-zinc-700 text-white font-bold">Draft</span>
              )}
            </div>
            <div className="font-display font-black text-xl mt-1">{form.title || "New case"}</div>
          </div>
          <button type="button" onClick={onClose} className="text-white hover:text-red-500 text-xl" aria-label="Close">
            <X size={22}/>
          </button>
        </div>

        {!isNew && (initial.status_history?.length > 0) && (
          <div className="border-b border-zinc-200 bg-zinc-50 px-5 py-2">
            <button
              type="button"
              onClick={() => setHistoryOpen((v) => !v)}
              className="text-[11px] uppercase tracking-widest font-bold text-zinc-700 inline-flex items-center gap-1 hover:text-red-600"
              data-testid="admin-active-history-toggle"
            >
              <History size={12}/> Status history ({initial.status_history.length}) {historyOpen ? "▴" : "▾"}
            </button>
            {historyOpen && (
              <ol className="mt-2 space-y-1 text-xs text-zinc-700 font-mono" data-testid="admin-active-history-list">
                {initial.status_history.map((h, i) => (
                  <li key={i} className="flex items-center gap-2">
                    <span className="inline-block w-24 font-bold">{h.status}</span>
                    <span className="text-zinc-500">{h.changed_at ? new Date(h.changed_at).toLocaleString() : ""}</span>
                    {h.note && <span className="text-zinc-500">— {h.note}</span>}
                  </li>
                ))}
              </ol>
            )}
          </div>
        )}

        <form onSubmit={(e) => save(e)} className="p-5 space-y-4 text-sm">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div className="sm:col-span-2">
              <label className="label-eyebrow block mb-1">Title *</label>
              <input className="field" value={form.title} onChange={set("title")} required data-testid="admin-active-edit-title"/>
            </div>
            <div>
              <label className="label-eyebrow block mb-1">Status</label>
              <select className="field" value={form.status} onChange={set("status")} data-testid="admin-active-edit-status">
                {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
              {!isNew && isPublished && form.status !== initial.status && (
                <p className="text-[11px] text-amber-700 mt-1">
                  Status change will apply only after you re-publish.
                </p>
              )}
            </div>
            <div>
              <label className="label-eyebrow block mb-1">Incident date *</label>
              <input type="date" className="field" value={form.incident_date} onChange={set("incident_date")} required/>
            </div>
            <div>
              <label className="label-eyebrow block mb-1">Incident time</label>
              <input className="field" value={form.incident_time} onChange={set("incident_time")} placeholder="approximately 9:15 AM"/>
            </div>
            <div className="sm:col-span-2">
              <label className="label-eyebrow block mb-1">Incident location</label>
              <input className="field" value={form.incident_location} onChange={set("incident_location")} placeholder="900 block of W. Park Lane"/>
            </div>
            <div>
              <label className="label-eyebrow block mb-1">City *</label>
              <input className="field" value={form.city} onChange={set("city")} required/>
            </div>
            <div>
              <label className="label-eyebrow block mb-1">State *</label>
              <input className="field" value={form.state} onChange={set("state")} required/>
            </div>
            <div>
              <label className="label-eyebrow block mb-1">State code</label>
              <input className="field" value={form.state_code} onChange={set("state_code")} maxLength={2}/>
            </div>
            <div className="sm:col-span-2">
              <label className="label-eyebrow block mb-1">Animal(s) *</label>
              <input className="field" value={form.animals_affected} onChange={set("animals_affected")} required placeholder="9-month-old black-and-white pit bull mix"/>
            </div>
            <div className="sm:col-span-2">
              <label className="label-eyebrow block mb-1">Summary *</label>
              <textarea rows={4} className="field" value={form.summary} onChange={set("summary")} required data-testid="admin-active-edit-summary"/>
            </div>
            <div className="sm:col-span-2">
              <label className="label-eyebrow block mb-1">Suspect description (only if officially released)</label>
              <input className="field" value={form.suspect_description} onChange={set("suspect_description")}/>
            </div>
            <div className="sm:col-span-2">
              <label className="label-eyebrow block mb-1">Vehicle description</label>
              <input className="field" value={form.vehicle_description} onChange={set("vehicle_description")}/>
            </div>
            <div>
              <label className="label-eyebrow block mb-1">License plate</label>
              <input className="field" value={form.license_plate} onChange={set("license_plate")}/>
            </div>
            <div>
              <label className="label-eyebrow block mb-1">Case / report #</label>
              <input className="field" value={form.case_number} onChange={set("case_number")}/>
            </div>
            <div>
              <label className="label-eyebrow block mb-1">Agency name *</label>
              <input className="field" value={form.agency_name} onChange={set("agency_name")} required/>
            </div>
            <div>
              <label className="label-eyebrow block mb-1">Agency phone</label>
              <input className="field" value={form.agency_phone} onChange={set("agency_phone")} placeholder="714-245-8378"/>
            </div>
            <div className="sm:col-span-2">
              <label className="label-eyebrow block mb-1">Agency tip URL</label>
              <input className="field" value={form.agency_tip_url} onChange={set("agency_tip_url")} placeholder="https://..."/>
            </div>
            <div className="sm:col-span-2">
              <label className="label-eyebrow block mb-1">Source URL (agency / news)</label>
              <input className="field" value={form.source_url} onChange={set("source_url")}/>
            </div>
          </div>

          <OfficialPhotoPanel
            caseId={initial.id}
            isNew={isNew}
            headers={headers}
            form={form}
            setForm={setForm}
          />

          {/* Case updates editor — admin can post dated updates (e.g. "Suspect
              identified and arrested"). Updates are rendered chronologically
              on the public page and trigger a status-update alert when the
              status moves alongside them. */}
          <div className="border-t border-zinc-200 pt-4">
            <div className="label-eyebrow mb-2">Case updates</div>
            <p className="text-[11px] text-zinc-500 mb-3 leading-relaxed">
              Post dated updates as the case progresses. The newest appears first on the public page.
            </p>
            {(form.case_updates || []).length === 0 && (
              <div className="text-xs text-zinc-500 italic mb-3">No case updates yet.</div>
            )}
            <div className="space-y-2 mb-3" data-testid="admin-active-updates-list">
              {(form.case_updates || []).map((u, i) => (
                <div key={i} className="border border-zinc-300 bg-zinc-50 p-3 flex items-start gap-3">
                  <div className="flex-1">
                    <div className="text-[10px] font-mono font-bold uppercase tracking-widest text-zinc-700">{u.date}</div>
                    <div className="text-sm mt-1 text-zinc-900">{u.text}</div>
                  </div>
                  <button
                    type="button"
                    onClick={() => removeCaseUpdate(i)}
                    className="text-red-700 hover:text-red-900"
                    aria-label="Remove update"
                    data-testid={`admin-active-update-remove-${i}`}
                  >
                    <X size={14}/>
                  </button>
                </div>
              ))}
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-6 gap-2">
              <input
                type="date"
                className="field sm:col-span-2"
                value={newUpdateDate}
                onChange={(e) => setNewUpdateDate(e.target.value)}
                data-testid="admin-active-new-update-date"
              />
              <input
                className="field sm:col-span-3"
                placeholder="e.g. Suspect identified and arrested; case pending."
                value={newUpdateText}
                onChange={(e) => setNewUpdateText(e.target.value)}
                data-testid="admin-active-new-update-text"
              />
              <button
                type="button"
                onClick={addCaseUpdate}
                className="inline-flex items-center justify-center gap-1 bg-zinc-900 text-white text-xs font-bold uppercase tracking-widest px-3 py-2 hover:bg-red-600"
                data-testid="admin-active-new-update-add"
              >
                <Plus size={12}/> Add
              </button>
            </div>
          </div>

          <div className="flex items-center justify-end gap-2 pt-2 border-t border-zinc-200 sticky bottom-0 bg-white pb-2 -mx-5 px-5 flex-wrap">
            <button type="button" onClick={onClose} className="btn-outline text-xs">Cancel</button>
            <button
              type="submit"
              disabled={saving}
              className="inline-flex items-center gap-1 text-xs font-bold uppercase tracking-widest px-3 py-2 border border-zinc-900 bg-white text-zinc-900 hover:bg-zinc-100 disabled:opacity-60"
              data-testid="admin-active-edit-save"
            >
              {saving ? <Loader2 size={12} className="animate-spin"/> : <Check size={12}/>}
              {isPublished ? "Save as pending edits" : (isNew ? "Save as draft" : "Save draft")}
            </button>
            <button
              type="button"
              disabled={saving}
              onClick={(e) => save(e, { andPublish: true })}
              className="inline-flex items-center gap-1 text-xs font-bold uppercase tracking-widest px-3 py-2 bg-red-600 text-white border border-red-700 hover:bg-red-700 disabled:opacity-60"
              data-testid="admin-active-edit-save-publish"
            >
              {saving ? <Loader2 size={12} className="animate-spin"/> : <Eye size={12}/>}
              {isPublished ? "Save & Re-publish" : "Save & Publish"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function OfficialPhotoPanel({ caseId, isNew, headers, form, setForm }) {
  const [mode, setMode] = useState("url"); // "url" | "upload"
  const [urlDraft, setUrlDraft] = useState(form.photo_url || "");
  const [sourceDraft, setSourceDraft] = useState(form.photo_source_url || "");
  const [attribDraft, setAttribDraft] = useState(form.photo_attribution || "");
  const [captionDraft, setCaptionDraft] = useState(form.photo_caption || "");
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);

  const BACKEND = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/+$/, "");
  const previewSrc = (() => {
    const u = form.photo_url || "";
    if (!u) return "";
    if (u.startsWith("/api/")) return `${BACKEND}${u}`;
    return u;
  })();

  const commitLocal = (fields) => {
    setForm((f) => ({ ...f, ...fields }));
  };

  const saveUrl = async () => {
    if (!urlDraft.trim()) { toast.error("Paste the direct image URL."); return; }
    if (!sourceDraft.trim()) { toast.error("Source URL is required (official release / article link)."); return; }
    if (attribDraft.trim().length < 3) { toast.error("Attribution note is required."); return; }
    if (isNew || !caseId) {
      // No case saved yet — just stash locally; it will persist when the admin saves the draft.
      commitLocal({
        photo_url: urlDraft.trim(),
        photo_source_url: sourceDraft.trim(),
        photo_attribution: attribDraft.trim(),
        photo_caption: captionDraft.trim(),
      });
      toast.success("Photo staged. It will save when you click Save draft.");
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post(
        `/admin/active-cases/${caseId}/photo`,
        {
          photo_url: urlDraft.trim(),
          photo_source_url: sourceDraft.trim(),
          photo_attribution: attribDraft.trim(),
          photo_caption: captionDraft.trim(),
        },
        { headers },
      );
      commitLocal({
        photo_url: data.photo_url,
        photo_source_url: data.photo_source_url,
        photo_attribution: data.photo_attribution,
        photo_caption: data.photo_caption,
      });
      toast.success(data.staged ? "Photo staged as pending edit." : "Photo attached.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to attach photo.");
    } finally {
      setBusy(false);
    }
  };

  const saveUpload = async () => {
    if (!file) { toast.error("Choose an image file first."); return; }
    if (!sourceDraft.trim()) { toast.error("Source URL is required (official release / article link)."); return; }
    if (attribDraft.trim().length < 3) { toast.error("Attribution note is required."); return; }
    if (isNew || !caseId) {
      toast.error("Save the draft first, then come back to upload the photo.");
      return;
    }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("photo_source_url", sourceDraft.trim());
      fd.append("photo_attribution", attribDraft.trim());
      fd.append("photo_caption", captionDraft.trim());
      const { data } = await api.post(
        `/admin/active-cases/${caseId}/photo/upload`,
        fd,
        { headers: { ...headers, "Content-Type": "multipart/form-data" } },
      );
      commitLocal({
        photo_url: data.photo_url,
        photo_source_url: data.photo_source_url,
        photo_attribution: data.photo_attribution,
        photo_caption: data.photo_caption,
      });
      setUrlDraft(data.photo_url);
      setFile(null);
      toast.success(data.staged ? "Photo uploaded as pending edit." : "Photo uploaded.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Upload failed.");
    } finally {
      setBusy(false);
    }
  };

  const removePhoto = async () => {
    if (!window.confirm("Remove the attached photo and its provenance?")) return;
    if (isNew || !caseId) {
      commitLocal({ photo_url: "", photo_source_url: "", photo_attribution: "", photo_caption: "" });
      setUrlDraft(""); setSourceDraft(""); setAttribDraft(""); setCaptionDraft("");
      return;
    }
    setBusy(true);
    try {
      await api.delete(`/admin/active-cases/${caseId}/photo`, { headers });
      commitLocal({ photo_url: "", photo_source_url: "", photo_attribution: "", photo_caption: "" });
      setUrlDraft(""); setSourceDraft(""); setAttribDraft(""); setCaptionDraft("");
      toast.success("Photo removed.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to remove photo.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="border-t border-zinc-200 pt-4" data-testid="admin-active-photo-panel">
      <div className="label-eyebrow mb-1 flex items-center gap-2">
        Official photo
        {form.photo_url && (
          <span className="text-[10px] uppercase tracking-widest font-bold px-2 py-0.5 border border-green-700 bg-green-50 text-green-800">
            Attached
          </span>
        )}
      </div>
      <p className="text-[11px] text-zinc-500 mb-3 leading-relaxed">
        Attach a photo only when it comes from an official or clearly permitted source (agency press release,
        sheriff/PD post, CrimeStoppers, official newsroom with reuse rights). Every photo must carry a source URL
        and a short attribution note — these are stored for provenance.
      </p>

      {form.photo_url && (
        <div className="mb-4 flex items-start gap-3 border border-zinc-300 bg-zinc-50 p-2">
          <img
            src={previewSrc}
            alt=""
            className="w-28 h-28 object-cover border border-zinc-300 bg-white"
            referrerPolicy="no-referrer"
          />
          <div className="flex-1 min-w-0 text-[11px] text-zinc-700 space-y-0.5">
            <div className="truncate"><b>Source:</b> {form.photo_source_url || "—"}</div>
            <div className="truncate"><b>Attribution:</b> {form.photo_attribution || "—"}</div>
            {form.photo_caption && <div className="truncate"><b>Caption:</b> {form.photo_caption}</div>}
          </div>
          <button
            type="button"
            onClick={removePhoto}
            disabled={busy}
            className="text-[11px] font-bold uppercase tracking-widest px-2 py-1 border border-red-700 text-red-700 hover:bg-red-50 disabled:opacity-60"
            data-testid="admin-active-photo-remove"
          >
            Remove
          </button>
        </div>
      )}

      <div className="inline-flex border border-zinc-900 mb-3 text-[11px] uppercase tracking-widest font-bold">
        <button
          type="button"
          onClick={() => setMode("url")}
          className={`px-3 py-1.5 ${mode === "url" ? "bg-zinc-900 text-white" : "bg-white text-zinc-700"}`}
          data-testid="admin-active-photo-tab-url"
        >
          Paste URL
        </button>
        <button
          type="button"
          onClick={() => setMode("upload")}
          className={`px-3 py-1.5 border-l border-zinc-900 ${mode === "upload" ? "bg-zinc-900 text-white" : "bg-white text-zinc-700"}`}
          data-testid="admin-active-photo-tab-upload"
        >
          Upload file
        </button>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        {mode === "url" ? (
          <div className="sm:col-span-2">
            <label className="label-eyebrow block mb-1">Direct image URL *</label>
            <input
              className="field"
              value={urlDraft}
              onChange={(e) => setUrlDraft(e.target.value)}
              placeholder="https://www.sheriff.gov/press/photos/case-7788.jpg"
              data-testid="admin-active-photo-url-input"
            />
          </div>
        ) : (
          <div className="sm:col-span-2">
            <label className="label-eyebrow block mb-1">Image file (PNG / JPG / WEBP, max 5 MB) *</label>
            <input
              type="file"
              accept="image/png,image/jpeg,image/webp,image/gif"
              onChange={(e) => setFile(e.target.files?.[0] || null)}
              className="block w-full text-xs"
              data-testid="admin-active-photo-file-input"
            />
            {isNew && (
              <p className="text-[11px] text-amber-700 mt-1">
                Save the draft first — uploads attach to an existing case.
              </p>
            )}
          </div>
        )}
        <div className="sm:col-span-2">
          <label className="label-eyebrow block mb-1">Source URL *</label>
          <input
            className="field"
            value={sourceDraft}
            onChange={(e) => setSourceDraft(e.target.value)}
            placeholder="https://www.sheriff.gov/press/2026/case-7788"
            data-testid="admin-active-photo-source-input"
          />
          <p className="text-[11px] text-zinc-500 mt-1">
            Where this photo was published. Required for provenance.
          </p>
        </div>
        <div className="sm:col-span-2">
          <label className="label-eyebrow block mb-1">Attribution / permission note *</label>
          <input
            className="field"
            value={attribDraft}
            onChange={(e) => setAttribDraft(e.target.value)}
            placeholder='e.g. "Cook County Sheriff press release, 10/4/26 — released for public ID"'
            data-testid="admin-active-photo-attribution-input"
          />
        </div>
        <div className="sm:col-span-2">
          <label className="label-eyebrow block mb-1">Caption (optional, shown with the image)</label>
          <input
            className="field"
            value={captionDraft}
            onChange={(e) => setCaptionDraft(e.target.value)}
            data-testid="admin-active-photo-caption-input"
          />
        </div>
      </div>

      <div className="flex justify-end mt-3">
        <button
          type="button"
          disabled={busy}
          onClick={mode === "url" ? saveUrl : saveUpload}
          className="inline-flex items-center gap-1 text-xs font-bold uppercase tracking-widest px-3 py-2 bg-zinc-900 text-white hover:bg-red-600 disabled:opacity-60"
          data-testid="admin-active-photo-save"
        >
          {busy ? <Loader2 size={12} className="animate-spin"/> : <Check size={12}/>}
          {mode === "url" ? "Attach URL" : "Upload & Attach"}
        </button>
      </div>
    </div>
  );
}

